import { useEffect, useRef } from 'react'
import { RefreshCw, ArrowLeft, Home, AlertCircle, LogOut } from 'lucide-react'
import { env } from '@/lib/env'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'

interface ErrorWithResponse extends Error {
  response?: { status: number }
  code?: string
}

interface ErrorFallbackProps {
  error: ErrorWithResponse
  resetErrorBoundary: () => void
  variant?: 'page' | 'section' | 'inline'
  title?: string
  section?: string
  showNavigateBack?: boolean
  showNavigateHome?: boolean
}

interface ErrorConfig {
  icon: string
  title: string
  message: string
  details?: string
  showRetry: boolean
  cardClass?: string
}

function getErrorConfig(
  error: ErrorWithResponse,
  section?: string
): ErrorConfig {
  const isAccessDenied =
    error?.response?.status === 403 ||
    error?.message?.toLowerCase().includes('access denied') ||
    error?.message?.toLowerCase().includes('zugriff verweigert')

  const isNotFound = error?.response?.status === 404
  const isServerError = error?.response?.status && error.response.status >= 500
  const isNetworkError = error?.code === 'ERR_NETWORK'

  if (isAccessDenied) {
    return {
      icon: '🔒',
      title: 'Zugriff verweigert',
      message: 'Sie haben keine Berechtigung für diese Funktion.',
      details:
        'Kontaktieren Sie Ihren Administrator, um Zugriff zu erhalten.',
      showRetry: false,
      cardClass: 'border-orange-200 dark:border-orange-800',
    }
  }

  if (isNotFound) {
    return {
      icon: '🔍',
      title: 'Nicht gefunden',
      message: 'Die angeforderte Ressource konnte nicht gefunden werden.',
      details: 'Bitte überprüfen Sie die URL oder versuchen Sie es später erneut.',
      showRetry: true,
    }
  }

  if (isServerError) {
    return {
      icon: '⚠️',
      title: 'Serverfehler',
      message: 'Ein interner Fehler ist aufgetreten.',
      details: 'Bitte versuchen Sie es später erneut oder kontaktieren Sie den Support.',
      showRetry: true,
    }
  }

  if (isNetworkError) {
    return {
      icon: '🌐',
      title: 'Netzwerkfehler',
      message: 'Verbindung zum Server konnte nicht hergestellt werden.',
      details: 'Bitte überprüfen Sie Ihre Internetverbindung.',
      showRetry: true,
    }
  }

  return {
    icon: '❌',
    title: section ? `Fehler beim Laden: ${section}` : 'Fehler aufgetreten',
    message: error?.message || 'Ein unerwarteter Fehler ist aufgetreten.',
    details: 'Bitte versuchen Sie es erneut oder kontaktieren Sie den Support.',
    showRetry: true,
  }
}

export function ErrorFallback({
  error,
  resetErrorBoundary,
  variant = 'section',
  title,
  section,
}: ErrorFallbackProps) {
  const errorConfig = getErrorConfig(error, section)
  const displayTitle = title || errorConfig.title

  // Inline variant - minimal display for non-critical sections
  if (variant === 'inline') {
    return (
      <div className="p-4 border-l-4 border-orange-500 bg-orange-50 dark:bg-orange-950/20 rounded-r">
        <div className="flex items-start gap-3">
          <span className="text-lg">{errorConfig.icon}</span>
          <div className="flex-1">
            <p className="text-sm font-medium text-orange-800 dark:text-orange-200">
              {displayTitle}
            </p>
            <p className="text-sm text-orange-700 dark:text-orange-300 mt-1">
              {errorConfig.message}
            </p>
            {errorConfig.showRetry && (
              <Button
                onClick={resetErrorBoundary}
                variant="link"
                size="sm"
                className="px-0 h-auto text-orange-700 dark:text-orange-300"
              >
                Erneut versuchen
              </Button>
            )}
          </div>
        </div>
      </div>
    )
  }

  // Page variant - full-page error display
  if (variant === 'page') {
    const handleLogout = () => {
      const logoutUrl = `https://${env.AUTH0_DOMAIN}/v2/logout?client_id=${env.AUTH0_CLIENT_ID}&returnTo=${encodeURIComponent(window.location.origin)}`
      window.location.href = logoutUrl
    }

    return (
      <div className="absolute inset-0 flex items-center justify-center p-6 bg-background">
        <Card className={`max-w-md w-full ${errorConfig.cardClass || ''}`}>
          <CardContent className="p-8">
            <div className="flex flex-col items-center gap-6 text-center">
              <AlertCircle className="h-12 w-12 text-destructive" />
              <div className="space-y-3">
                <h1 className="text-2xl font-bold">{displayTitle}</h1>
                <p className="text-muted-foreground">{errorConfig.message}</p>
                {errorConfig.details && (
                  <p className="text-sm text-muted-foreground">
                    {errorConfig.details}
                  </p>
                )}
              </div>
              <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:flex-wrap sm:justify-center">
                <Button
                  onClick={() => window.history.back()}
                  className="gap-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Zurück
                </Button>
                <Button
                  variant="outline"
                  onClick={() => (window.location.href = '/')}
                  className="gap-2"
                >
                  <Home className="h-4 w-4" />
                  Startseite
                </Button>
                <Button
                  variant="outline"
                  onClick={handleLogout}
                  className="gap-2"
                >
                  <LogOut className="h-4 w-4" />
                  Abmelden
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Section variant (default) - card-based display for page sections
  return (
    <Card className={errorConfig.cardClass || ''}>
      <CardContent className="p-6">
        <div className="flex items-start gap-4">
          <span className="text-2xl flex-shrink-0">{errorConfig.icon}</span>
          <div className="flex-1 space-y-3">
            <div>
              <h3 className="font-semibold text-lg">{displayTitle}</h3>
              <p className="text-muted-foreground mt-1">{errorConfig.message}</p>
            </div>
            {errorConfig.details && (
              <p className="text-sm text-muted-foreground">
                {errorConfig.details}
              </p>
            )}
            {errorConfig.showRetry && (
              <Button
                onClick={resetErrorBoundary}
                size="sm"
                variant="outline"
                className="gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Erneut versuchen
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

const MAX_TRANSLATION_RECOVERIES = 3

function isTranslationDomError(error?: { name?: string; message?: string }): boolean {
  const message = error?.message ?? ''
  return (
    error?.name === 'NotFoundError' &&
    (message.includes('removeChild') || message.includes('insertBefore'))
  )
}

/**
 * Route-level error component for TanStack Router errorComponent prop
 */
export function RouteErrorFallback({
  error,
  reset,
}: {
  error: Error
  reset: () => void
}) {
  const recoveryAttempts = useRef(0)
  const isTranslationError = isTranslationDomError(error)

  useEffect(() => {
    if (isTranslationError && recoveryAttempts.current < MAX_TRANSLATION_RECOVERIES) {
      recoveryAttempts.current += 1
      reset()
    }
  })

  if (isTranslationError && recoveryAttempts.current < MAX_TRANSLATION_RECOVERIES) {
    return null
  }

  return (
    <div className="container mx-auto p-6">
      <ErrorFallback
        error={error as ErrorWithResponse}
        resetErrorBoundary={reset}
        variant="page"
        showNavigateBack
        showNavigateHome
      />
    </div>
  )
}
